import * as THREE from 'three';
import type { MaterialManager } from '../assets/MaterialManager';
import { generateProceduralPBRSet, type ProceduralPBRSet } from './proceduralTexture';

/**
 * Every PBR material recipe for course surfaces lives here — this is the
 * one place to look when a surface looks wrong or needs a new variant.
 * MaterialLibrary is the golf-specific *data* (what roughness/color/noise
 * pattern makes "fairway grass" look right); MaterialManager (assets/) is
 * the generic *cache mechanism* it's built on, same mechanism/data split
 * as LightingSystem/LightingConfig.
 *
 * Texture maps are procedurally generated (see proceduralTexture.ts) — no
 * photographic texture assets exist in this project. AO is baked directly
 * into the albedo map rather than wired up as a separate `aoMap`: three
 * requires a second UV channel (`uv2`) for `aoMap`, which would mean every
 * geometry that wants AO (terrain, props, vegetation) needs a uv2
 * attribute added — baking AO into albedo avoids that requirement across
 * every consumer of this library, at the honest cost of AO no longer being
 * independently swappable. Normal and roughness maps *are* real, separate,
 * runtime-sampled maps (they use the primary UV channel, no uv2 needed).
 */
export interface SurfaceRecipe {
  color: number;
  roughness: number;
  variation: number;
  bumpStrength: number;
  roughnessVariation: number;
  noiseScale: number;
  stripeStrength?: number;
  stripeFrequency?: number;
  stripeAngleRad?: number;
}

export const SURFACE_RECIPES = {
  fairway: {
    color: 0x6aa34a,
    roughness: 0.78,
    variation: 0.14,
    bumpStrength: 1.1,
    roughnessVariation: 0.1,
    noiseScale: 22,
    stripeStrength: 0.12,
    stripeFrequency: 7,
    stripeAngleRad: -0.32,
  },
  rough: {
    color: 0x4f7a35,
    roughness: 0.98,
    variation: 0.3,
    bumpStrength: 2.35,
    roughnessVariation: 0.12,
    noiseScale: 16,
  },
  green: {
    // Lighter close-mown putting surface so the green edge reads clearly against fairway/rough.
    color: 0x94c96b,
    roughness: 0.54,
    variation: 0.045,
    bumpStrength: 0.38,
    roughnessVariation: 0.04,
    noiseScale: 38,
  },
  sand: {
    color: 0xdfc88f,
    roughness: 0.93,
    variation: 0.24,
    bumpStrength: 2.05,
    roughnessVariation: 0.13,
    noiseScale: 22,
  },
  dirt: {
    color: 0x6b5335,
    roughness: 0.92,
    variation: 0.25,
    bumpStrength: 1.8,
    roughnessVariation: 0.12,
    noiseScale: 9,
  },
  rock: {
    color: 0x767268,
    roughness: 0.75,
    variation: 0.35,
    bumpStrength: 2.6,
    roughnessVariation: 0.2,
    noiseScale: 6,
  },
} as const satisfies Record<string, SurfaceRecipe>;

export type SurfaceKind = keyof typeof SURFACE_RECIPES;

type TerrainLayerKind = 'fairway' | 'green' | 'sand' | 'rough';

export class MaterialLibrary {
  private readonly textureCache = new Map<SurfaceKind, ProceduralPBRSet>();

  constructor(private readonly materials: MaterialManager) {}

  private textureSetFor(kind: SurfaceKind): ProceduralPBRSet {
    const cached = this.textureCache.get(kind);
    if (cached) return cached;

    const recipe: SurfaceRecipe = SURFACE_RECIPES[kind];
    const set = generateProceduralPBRSet({
      baseColor: new THREE.Color(recipe.color),
      variation: recipe.variation,
      bumpStrength: recipe.bumpStrength,
      baseRoughness: recipe.roughness,
      roughnessVariation: recipe.roughnessVariation,
      noiseScale: recipe.noiseScale,
      stripeStrength: recipe.stripeStrength,
      stripeFrequency: recipe.stripeFrequency,
      stripeAngleRad: recipe.stripeAngleRad,
    });
    this.textureCache.set(kind, set);
    return set;
  }

  /** A full PBR material for a surface kind — used for standalone meshes (rock props, cart paths) that don't need terrain-style blending. */
  surface(kind: SurfaceKind): THREE.MeshStandardMaterial {
    return this.materials.getOrCreate(`surface:${kind}`, () => {
      const textures = this.textureSetFor(kind);
      const recipe = SURFACE_RECIPES[kind];
      return new THREE.MeshStandardMaterial({
        map: textures.albedo,
        normalMap: textures.normal,
        roughnessMap: textures.roughness,
        roughness: recipe.roughness,
        metalness: 0,
      });
    });
  }

  water(): THREE.MeshStandardMaterial {
    return this.materials.getOrCreate(
      'surface:water-fallback',
      () =>
        new THREE.MeshStandardMaterial({
          color: 0x2b5d73,
          roughness: 0.12,
          metalness: 0.15,
          transparent: true,
          opacity: 0.85,
        }),
    );
  }

  /** A procedurally generated, tileable water normal map for WaterRenderer's animated shading. */
  waterNormalMap(): THREE.CanvasTexture {
    if (this.waterNormalCache) return this.waterNormalCache;
    const set = generateProceduralPBRSet({
      baseColor: new THREE.Color(0x8080ff),
      variation: 0,
      bumpStrength: 3.2,
      baseRoughness: 0.1,
      roughnessVariation: 0,
      noiseScale: 8,
      size: 256,
    });
    this.waterNormalCache = set.normal;
    return set.normal;
  }

  private waterNormalCache: THREE.CanvasTexture | null = null;

  /**
   * The terrain's blended material: a MeshStandardMaterial (full PBR
   * lighting/shadow/environment response, for free) whose shader is
   * patched via onBeforeCompile to blend real per-layer albedo + roughness
   * textures (tiled at `detailRepeat`) by a splat texture's per-channel
   * weights, instead of Three's normal single `map`/`roughnessMap`
   * sampling. See TerrainRenderer for how the splat texture is built.
   *
   * Blends fairway/green/sand (explicit r/g/b splat channels) plus rough
   * (the implicit remainder wherever r+g+b < 1). Normal-map blending is
   * deliberately not attempted here: correctly blending multiple tangent-
   * space normal maps needs real per-vertex tangents and TBN-aware
   * compositing, which is meaningfully riskier to get right without a way
   * to visually verify it than the straightforward per-channel value
   * blends used for albedo/roughness below. Standalone (non-blended)
   * surfaces via surface() above do get real normal maps — this is a
   * scoped-down decision for the one shader that needs custom blending,
   * not a project-wide limitation.
   *
   * Dirt/rock are intentionally not part of this blend — see
   * SplatMapBaker's doc comment for why a 4th splat channel doesn't fit
   * cleanly on top of alpha-based paint compositing, and Hole has no
   * `dirt`/`rock` region data source yet either. Both are real, textured
   * standalone materials via surface() instead (used for rock props and
   * dirt paths — see CourseRenderer).
   */
  terrainMaterial(splatMap: THREE.Texture, detailRepeat = 1 / 6): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({ roughness: 1, metalness: 0 });

    const layerTextures: Record<TerrainLayerKind, ProceduralPBRSet> = {
      fairway: this.textureSetFor('fairway'),
      green: this.textureSetFor('green'),
      sand: this.textureSetFor('sand'),
      rough: this.textureSetFor('rough'),
    };

    const uniforms = {
      splatMap: { value: splatMap },
      detailRepeat: { value: detailRepeat },
      fairwayAlbedo: { value: layerTextures.fairway.albedo },
      fairwayRoughness: { value: layerTextures.fairway.roughness },
      greenAlbedo: { value: layerTextures.green.albedo },
      greenRoughness: { value: layerTextures.green.roughness },
      sandAlbedo: { value: layerTextures.sand.albedo },
      sandRoughness: { value: layerTextures.sand.roughness },
      roughAlbedo: { value: layerTextures.rough.albedo },
      roughRoughness: { value: layerTextures.rough.roughness },
    };

    material.defines = { ...material.defines, USE_UV: '' };

    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms);

      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
          uniform float detailRepeat;
          varying vec2 vDetailUv;`,
        )
        .replace(
          '#include <uv_vertex>',
          `#include <uv_vertex>
          vDetailUv = uv * detailRepeat;`,
        );

      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
          varying vec2 vDetailUv;
          uniform sampler2D splatMap;
          uniform sampler2D fairwayAlbedo;
          uniform sampler2D fairwayRoughness;
          uniform sampler2D greenAlbedo;
          uniform sampler2D greenRoughness;
          uniform sampler2D sandAlbedo;
          uniform sampler2D sandRoughness;
          uniform sampler2D roughAlbedo;
          uniform sampler2D roughRoughness;`,
        )
        .replace(
          '#include <map_fragment>',
          `vec3 splatWeights = texture2D( splatMap, vUv ).rgb;
          float fairwayW = splatWeights.r;
          float greenW = splatWeights.g;
          float sandW = splatWeights.b;
          float roughW = clamp( 1.0 - ( fairwayW + greenW + sandW ), 0.0, 1.0 );
          float splatTotal = max( 0.0001, fairwayW + greenW + sandW + roughW );

          vec3 blendedAlbedo =
            texture2D( fairwayAlbedo, vDetailUv ).rgb * fairwayW +
            texture2D( greenAlbedo,   vDetailUv ).rgb * greenW   +
            texture2D( sandAlbedo,    vDetailUv ).rgb * sandW    +
            texture2D( roughAlbedo,   vDetailUv ).rgb * roughW;
          blendedAlbedo /= splatTotal;

          diffuseColor.rgb *= blendedAlbedo;`,
        )
        .replace(
          '#include <roughnessmap_fragment>',
          `float blendedRoughnessValue =
            ( texture2D( fairwayRoughness, vDetailUv ).g * fairwayW +
              texture2D( greenRoughness,   vDetailUv ).g * greenW   +
              texture2D( sandRoughness,    vDetailUv ).g * sandW    +
              texture2D( roughRoughness,   vDetailUv ).g * roughW ) / splatTotal;
          float roughnessFactor = blendedRoughnessValue;`,
        );
    };

    return material;
  }
}
